import { faDot } from '@fortawesome/duotone-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC } from 'react';
import { type MeetingRoomRecommendation } from '../types';
import { RecommendationCard } from './RecommendationCard';

interface Props {
  readonly meetingRoom: MeetingRoomRecommendation;
}

export const MeetingRoomCard: FC<Props> = ({ meetingRoom }) => {
  const {
    name,
    description,
    location,
    type,
    dimensions,
    space_sq_ft: spaceSqFt,
    space_sq_m: spaceSqM,
    capacity_theater: theaterCapacity,
    capacity_classroom: classroomCapacity,
    capacity_conference: conferenceCapacity,
    capacity_u_shape: uShapeCapacity,
    capacity_hollow_square: hollowSquareCapacity,
    capacity_banquet_rounds: banquetCapacity,
    capacity_reception: receptionCapacity,
    website_link: websiteLink,
    virtual_tour_link: virtualTourLink,
    image_link: imageLink,
  } = meetingRoom;

  interface RawCapacity {
    readonly capacity: number | null;
    readonly label: string;
  }

  interface Capacity extends RawCapacity {
    readonly capacity: number;
  }

  const hasCapacity = (x: RawCapacity): x is Capacity => x.capacity !== null;

  const capacities = [
    { capacity: theaterCapacity, label: 'Theater' },
    { capacity: classroomCapacity, label: 'Classroom' },
    { capacity: conferenceCapacity, label: 'Conference' },
    { capacity: uShapeCapacity, label: 'U-Shape' },
    { capacity: hollowSquareCapacity, label: 'Hollow Square' },
    { capacity: banquetCapacity, label: 'Banquet' },
    { capacity: receptionCapacity, label: 'Reception' },
  ]
    .filter<Capacity>(hasCapacity)
    .sort((a, b) => b.capacity - a.capacity);

  return (
    <RecommendationCard
      image={imageLink ? { uri: imageLink, altText: 'TBD' } : null}
      name={name}
      url={websiteLink}
      description={description}
      location={location ? { name: location } : null}
      capacities={capacities}
      subtitleContent={
        <span className={'icon-text'}>
          <span>{type}</span>
          {dimensions && (
            <>
              <span className={'icon is-narrow'}>
                <FontAwesomeIcon icon={faDot} size={'lg'} />
              </span>
              <span> • {dimensions}</span>
            </>
          )}
          {spaceSqFt && (
            <>
              <span className={'icon is-narrow'}>
                <FontAwesomeIcon icon={faDot} size={'lg'} />
              </span>
              <span>
                {Math.round(spaceSqFt).toString()} ft²
                {spaceSqM ? ` / ${Math.round(spaceSqM).toString()} m²` : null}
              </span>
            </>
          )}
        </span>
      }
      virtualTourLink={virtualTourLink && virtualTourLink.trim() !== '' ? virtualTourLink : null}
    />
  );
};
MeetingRoomCard.displayName = 'MeetingRoomCard';
